/*
 * Rang Sitaray Quick View ("Have a Look").
 *  - One shared popup (#RsQuickView, snippets/rs-quick-view.liquid); every [data-rs-qv-open] button on the
 *    page opens it through a single delegated click handler, so grids that re-render keep working.
 *  - Product, variants, prices, availability and images come from /products/<handle>.js. Nothing is hardcoded.
 *  - Adds the chosen variant and quantity with Dawn's cart endpoint, updates the cart count, and keeps the
 *    popup open with a success message (a cart drawer, if the theme uses one, is opened instead).
 *  - Focus is trapped in the popup, Escape / backdrop / close button close it, and the page behind stays still.
 */
(() => {
  if (window.rsQuickViewReady) return;
  window.rsQuickViewReady = true;

  const cache = new Map();
  let root;
  let ui;
  let state = null;
  let opener = null;
  let moneyFormat = '{{amount}}';

  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- money ---------- */

  const group = (cents, decimals, thousands, point) => {
    const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
    return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fraction ? point + fraction : '');
  };

  const money = (cents) => {
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const kind = match ? match[1] : 'amount';
    let value;
    switch (kind) {
      case 'amount_no_decimals':
        value = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = group(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = group(cents, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = group(cents, 2, ' ', ',');
        break;
      default:
        value = group(cents, 2, ',', '.');
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /* ---------- product data ---------- */

  const optionNames = (product) =>
    (product.options || []).map((option) => (typeof option === 'string' ? option : option.name));

  const loadProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } }).then(
          (response) => {
            if (!response.ok) throw new Error('product');
            return response.json();
          }
        )
      );
      cache.get(handle).catch(() => cache.delete(handle));
    }
    return cache.get(handle);
  };

  const findVariant = (product, options) =>
    product.variants.find((variant) => options.every((value, index) => variant.options[index] === value));

  // A value is sold out when no available variant carries it together with the other chosen values.
  const valueAvailable = (product, index, value, options) =>
    product.variants.some(
      (variant) =>
        variant.available &&
        variant.options[index] === value &&
        options.every((chosen, i) => i === index || chosen == null || variant.options[i] === chosen)
    );

  /* ---------- rendering ---------- */

  const imageUrl = (src, width) => {
    if (!src) return '';
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  };

  const setImage = (variant, product) => {
    const featured = (variant && variant.featured_image) || null;
    const src = featured ? featured.src : product.featured_image || (product.images && product.images[0]);
    const alt = (featured && featured.alt) || product.title;
    if (!src) {
      ui.image.removeAttribute('src');
      ui.image.removeAttribute('srcset');
      return;
    }
    ui.image.src = imageUrl(src, 720);
    ui.image.srcset = `${imageUrl(src, 480)} 480w, ${imageUrl(src, 720)} 720w, ${imageUrl(src, 960)} 960w`;
    ui.image.sizes = '(min-width: 750px) 420px, 40vw';
    ui.image.alt = alt;
    if (featured && featured.width && featured.height) {
      ui.image.width = featured.width;
      ui.image.height = featured.height;
    }
  };

  const renderPrice = (variant, product) => {
    const price = variant ? variant.price : product.price;
    const compare = variant ? variant.compare_at_price : product.compare_at_price;
    const onSale = compare && compare > price;
    ui.current.textContent = money(price);
    ui.compare.hidden = !onSale;
    ui.save.hidden = !onSale;
    ui.price.classList.toggle('rs-qv__price--sale', Boolean(onSale));
    if (onSale) {
      ui.compare.textContent = money(compare);
      ui.save.textContent = `Save ${money(compare - price)}`;
    }
  };

  const renderOptions = () => {
    const { product, options } = state;
    const names = optionNames(product);
    const hideSingle = product.variants.length === 1 && product.variants[0].title === 'Default Title';
    ui.options.textContent = '';
    ui.options.hidden = hideSingle;
    if (hideSingle) return;

    names.forEach((name, index) => {
      const values = [];
      product.variants.forEach((variant) => {
        if (!values.includes(variant.options[index])) values.push(variant.options[index]);
      });

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'rs-qv__option';
      const legend = document.createElement('legend');
      legend.className = 'rs-qv__label';
      legend.textContent = name;
      const chosen = document.createElement('span');
      chosen.className = 'rs-qv__chosen';
      chosen.dataset.rsQvChosen = String(index);
      legend.append(chosen);
      fieldset.append(legend);

      const list = document.createElement('div');
      list.className = 'rs-qv__values';
      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rs-qv__value';
        button.dataset.index = String(index);
        button.dataset.value = value;
        button.textContent = value;
        list.append(button);
      });
      fieldset.append(list);
      ui.options.append(fieldset);
    });
    refreshOptions();
  };

  const refreshOptions = () => {
    const { product, options } = state;
    ui.options.querySelectorAll('.rs-qv__value').forEach((button) => {
      const index = Number(button.dataset.index);
      const value = button.dataset.value;
      const selected = options[index] === value;
      const available = valueAvailable(product, index, value, options);
      button.classList.toggle('is-selected', selected);
      button.classList.toggle('is-soldout', !available);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-label', `${value}${available ? '' : ` (${ui.root.dataset.labelSoldOut})`}`);
      button.title = available ? '' : ui.root.dataset.labelSoldOut;
    });
    ui.options.querySelectorAll('[data-rs-qv-chosen]').forEach((chosen) => {
      chosen.textContent = options[Number(chosen.dataset.rsQvChosen)] || '';
    });
  };

  const maxQuantity = () => 99;

  const refresh = (keepFeedback) => {
    const { product, options } = state;
    const complete = options.every((value) => value != null);
    const variant = complete ? findVariant(product, options) : null;
    state.variant = variant || null;

    refreshOptions();
    renderPrice(variant, product);
    setImage(variant, product);

    let status = '';
    let label = ui.root.dataset.labelAdd;
    let canAdd = false;
    if (!variant) {
      const anyAvailable = product.variants.some((item) => item.available);
      status = anyAvailable ? '' : ui.root.dataset.labelSoldOut;
      label = anyAvailable ? `Select ${optionNames(product)[options.findIndex((v) => v == null)] || 'an option'}` : ui.root.dataset.labelSoldOut;
    } else if (variant.available) {
      status = 'In stock';
      canAdd = true;
    } else {
      status = ui.root.dataset.labelSoldOut;
      label = ui.root.dataset.labelSoldOut;
    }
    ui.status.textContent = status;
    ui.status.classList.toggle('is-soldout', Boolean(variant && !variant.available) || (!variant && status !== ''));
    ui.add.disabled = !canAdd;
    ui.addLabel.textContent = label;
    ui.qtyInput.disabled = !canAdd;
    ui.minus.disabled = !canAdd || Number(ui.qtyInput.value) <= 1;
    ui.plus.disabled = !canAdd || Number(ui.qtyInput.value) >= maxQuantity();
    if (!keepFeedback) clearFeedback();
  };

  const clearFeedback = () => {
    ui.message.hidden = true;
    ui.message.textContent = '';
    ui.error.hidden = true;
    ui.error.textContent = '';
    ui.after.hidden = true;
  };

  const selectOption = (index, value) => {
    const { product } = state;
    const next = state.options.slice();
    next[index] = value;
    let variant = findVariant(product, next);
    if (!variant) {
      // This combination does not exist: move to the nearest variant that has the chosen value.
      const matching = product.variants.filter((item) => item.options[index] === value);
      variant = matching.find((item) => item.available) || matching[0];
    }
    state.options = variant ? variant.options.slice() : next;
    refresh();
  };

  const readQuantity = () => {
    const value = parseInt(ui.qtyInput.value, 10);
    return Number.isFinite(value) ? Math.min(Math.max(value, 1), maxQuantity()) : 1;
  };

  const setQuantity = (value) => {
    ui.qtyInput.value = String(Math.min(Math.max(value, 1), maxQuantity()));
    ui.minus.disabled = ui.add.disabled || Number(ui.qtyInput.value) <= 1;
    ui.plus.disabled = ui.add.disabled || Number(ui.qtyInput.value) >= maxQuantity();
    clearFeedback();
  };

  /* ---------- open / close ---------- */

  const focusables = () =>
    Array.from(
      ui.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.closest('[hidden]') && element.offsetParent !== null);

  const lockPage = () => {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--rs-qv-scrollbar', `${scrollbar}px`);
    document.documentElement.classList.add('rs-qv-lock');
    document.body.classList.add('rs-qv-lock');
  };

  const unlockPage = () => {
    document.documentElement.classList.remove('rs-qv-lock');
    document.body.classList.remove('rs-qv-lock');
    document.documentElement.style.removeProperty('--rs-qv-scrollbar');
  };

  const showPanel = (panel) => {
    ui.loading.hidden = panel !== 'loading';
    ui.fail.hidden = panel !== 'fail';
    ui.body.hidden = panel !== 'body';
  };

  const open = (button) => {
    const handle = button.dataset.handle;
    if (!handle) return;
    opener = button;
    state = null;
    delete ui.root.dataset.loadedHandle;
    clearFeedback();
    ui.root.hidden = false;
    showPanel('loading');
    lockPage();
    requestAnimationFrame(() => ui.root.classList.add('is-open'));
    ui.dialog.focus();
    ui.failLink.href = `${rootUrl()}products/${handle}`;

    loadProduct(handle)
      .then((product) => {
        if (ui.root.hidden || opener !== button) return;
        const firstAvailable = product.variants.find((variant) => variant.available);
        state = {
          product,
          // never start on a sold-out variant: with nothing available the customer must choose
          options: firstAvailable ? firstAvailable.options.slice() : optionNames(product).map(() => null),
          variant: null,
        };
        ui.title.textContent = (button.dataset.title || product.title).trim();
        ui.details.href = product.url || `${rootUrl()}products/${handle}`;
        ui.qtyInput.value = '1';
        renderOptions();
        showPanel('body');
        refresh();
        ui.root.dataset.loadedHandle = product.handle;
        ui.dialog.scrollTop = 0;
        const target = ui.options.querySelector('.rs-qv__value.is-selected') || ui.options.querySelector('.rs-qv__value') || ui.add;
        if (target && !target.disabled) target.focus({ preventScroll: true });
      })
      .catch(() => {
        if (ui.root.hidden) return;
        showPanel('fail');
      });
  };

  const close = () => {
    if (ui.root.hidden) return;
    ui.root.classList.remove('is-open');
    ui.root.hidden = true;
    unlockPage();
    state = null;
    if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    opener = null;
  };

  /* ---------- cart ---------- */

  const updateCartCount = (response) => {
    const bubble = document.getElementById('cart-icon-bubble');
    const html = response.sections && response.sections['cart-icon-bubble'];
    if (bubble && html) {
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (section) bubble.innerHTML = section.innerHTML;
    }
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      try {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'rs-quick-view', cartData: response });
      } catch (error) {
        /* other cart widgets are optional */
      }
    }
  };

  const addToCart = async () => {
    if (!state || !state.variant || !state.variant.available || ui.add.getAttribute('aria-busy') === 'true') return;
    const quantity = readQuantity();
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];

    clearFeedback();
    ui.add.setAttribute('aria-busy', 'true');
    ui.add.disabled = true;
    ui.addLabel.textContent = 'Adding…';

    try {
      const url = (window.routes && window.routes.cart_add_url) || `${rootUrl()}cart/add`;
      const response = await fetch(`${url}${url.endsWith('.js') ? '' : '.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: state.variant.id, quantity, sections, sections_url: window.location.pathname }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        ui.error.textContent = data.description || data.message || 'This item could not be added. Please try again.';
        ui.error.hidden = false;
        return;
      }

      const name = ui.title.textContent;
      const detail = state.variant.title && state.variant.title !== 'Default Title' ? ` (${state.variant.title})` : '';
      if (drawer && typeof drawer.renderContents === 'function') {
        const returnTo = opener;
        close();
        if (returnTo && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(returnTo);
        drawer.renderContents(data);
        return;
      }
      updateCartCount(data);
      ui.message.textContent = `Added to cart: ${quantity} × ${name}${detail}.`;
      ui.message.hidden = false;
      ui.after.hidden = false;
    } catch (error) {
      ui.error.textContent = 'Something went wrong. Please check your connection and try again.';
      ui.error.hidden = false;
    } finally {
      ui.add.removeAttribute('aria-busy');
      if (!ui.root.hidden && state) {
        refresh(true);
        // stock can change after adding, so reload the product and refresh availability
        const { handle } = state.product;
        cache.delete(handle);
        loadProduct(handle)
          .then((product) => {
            if (state && !ui.root.hidden && state.product.handle === handle) {
              state.product = product;
              refresh(true);
            }
          })
          .catch(() => {});
      }
    }
  };

  /* ---------- wiring (once) ---------- */

  const init = () => {
    root = document.getElementById('RsQuickView');
    if (!root) return;
    moneyFormat = root.dataset.moneyFormat || moneyFormat;
    const q = (selector) => root.querySelector(selector);
    ui = {
      root,
      dialog: q('.rs-qv__dialog'),
      loading: q('[data-rs-qv-loading]'),
      fail: q('[data-rs-qv-fail]'),
      failLink: q('[data-rs-qv-fail-link]'),
      body: q('[data-rs-qv-body]'),
      image: q('[data-rs-qv-image]'),
      title: q('[data-rs-qv-title]'),
      price: q('[data-rs-qv-price]'),
      compare: q('[data-rs-qv-compare]'),
      current: q('[data-rs-qv-current]'),
      save: q('[data-rs-qv-save]'),
      options: q('[data-rs-qv-options]'),
      status: q('[data-rs-qv-status]'),
      qtyInput: q('[data-rs-qv-qty]'),
      minus: q('[data-rs-qv-minus]'),
      plus: q('[data-rs-qv-plus]'),
      add: q('[data-rs-qv-add]'),
      addLabel: q('[data-rs-qv-add-label]'),
      message: q('[data-rs-qv-message]'),
      error: q('[data-rs-qv-error]'),
      after: q('[data-rs-qv-after]'),
      details: q('[data-rs-qv-details]'),
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-rs-qv-open]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        open(trigger);
        return;
      }
      if (!root.hidden && event.target.closest('[data-rs-qv-close]')) close();
    });

    document.addEventListener('keydown', (event) => {
      if (root.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        const items = focusables();
        if (items.length === 0) {
          event.preventDefault();
          ui.dialog.focus();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === ui.dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    ui.options.addEventListener('click', (event) => {
      const button = event.target.closest('.rs-qv__value');
      if (button && state) selectOption(Number(button.dataset.index), button.dataset.value);
    });
    ui.minus.addEventListener('click', () => setQuantity(readQuantity() - 1));
    ui.plus.addEventListener('click', () => setQuantity(readQuantity() + 1));
    ui.qtyInput.addEventListener('change', () => setQuantity(readQuantity()));
    ui.qtyInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        setQuantity(readQuantity());
      }
    });
    ui.add.addEventListener('click', addToCart);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
